import forge from 'apis/forge';
import { IconType } from 'components/Icon';
import ActionButton, { ActionButtonState } from 'components/Widget/Button/ActionButton';
import Pill from 'components/Widget/Pill';
import SubmitButton, { SubmitButtonState } from 'components/Widget/Button/SubmitButton';
import ChatAttachmentTile, { ChatAttachmentTileState } from 'components/Widget/ChatAttachmentTile';
import MultilineInput from 'components/Widget/Input/MultilineInput';
import { Loader } from 'components/Widget/Loader';
import { dsPickToNavTarget, dsTabToPick, resolveDsPicksToDisplayNames } from 'designSystem/content/util';
import * as api from 'dragonmouse-shared/api/forge';
import { ChatMode, ChatStatus, DsChat, DsPick } from 'dragonmouse-shared/interfaces';
import { interceptIfNoCredits } from 'economy';
import { useFileUploadCallbacks } from 'hooks/useFileUploadCallbacks';
import useToast from 'hooks/useToast';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { selectActiveDesignSystem } from 'redux/db/reducers';
import { addDsTab, clearDsPicks, removeDsPick, setActiveDsTab, setPendingDsChatInput } from 'redux/mode/actions';
import { selectActiveDsTab, selectIsMobile } from 'redux/mode/reducers';
import { RootReducer } from 'redux/reducers';
import { useTheme } from 'styled-components';
import * as routes from 'routes';
import { onFilePasted, onUploadFileDialog } from 'utils';
import { useChatInputHandlers } from '../../hooks/useChatInputHandlers';
import { useChatInputValidation } from '../../hooks/useChatInputValidation';
import * as Base from '../styles';
import BreakageNotification from './BreakageNotification';

interface DsChatInputProps {
	dsChat: DsChat;
}

const DsChatInput = ({ dsChat }: DsChatInputProps) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const theme = useTheme();
	const { message: toastMessage, isVisible: isToastVisible, showToast } = useToast();

	const { activeDesignSystem, pendingDsChatInput, isMobile, selectedDsPicks, designSystemContent, dsTabs, activeTab } =
		useSelector((r: RootReducer) => {
			return {
				activeDesignSystem: selectActiveDesignSystem(r.db, r.mode.activeDesignSystemId),
				pendingDsChatInput: r.mode.pendingDsChatInputs[dsChat.id],
				isMobile: selectIsMobile(r.mode),
				selectedDsPicks: r.mode.selectedDsPicks,
				designSystemContent: r.mode.designSystemContent,
				dsTabs: r.mode.dsTabs,
				activeTab: selectActiveDsTab(r.mode),
			};
		}, shallowEqual);
	const [prompt, setPrompt] = useState<string>(pendingDsChatInput?.message || '');
	const [isFocused, setIsFocused] = useState<boolean>(false);
	const inputRef = useRef<HTMLTextAreaElement>(null);

	useEffect(() => {
		if (!pendingDsChatInput) {
			dispatch(
				setPendingDsChatInput({
					dsChatId: dsChat.id,
					pendingDsChatInput: {
						message: '',
						chatMode: ChatMode.DESIGN_AND_CHAT,
					},
				})
			);
		}
	}, [dsChat.id, pendingDsChatInput, dispatch]);

	const stoppable = dsChat?.metadata?.status === ChatStatus.STOPPABLE;
	const showSpinner = dsChat?.metadata?.status === ChatStatus.PROCESSING;

	// Validation logic for whether input can be submitted
	const { canSubmit } = useChatInputValidation({
		prompt,
		loadingAttachments: pendingDsChatInput?.loadingAttachments,
	});

	const resolvedDsPicks = useMemo(
		() => resolveDsPicksToDisplayNames(selectedDsPicks, designSystemContent ?? null),
		[selectedDsPicks, designSystemContent]
	);

	//------------------------------------------------------------------------------------------------
	// HANDLERS
	//------------------------------------------------------------------------------------------------
	const onSubmit = async () => {
		if (!activeDesignSystem || !dsChat) return;

		// Check credits/wallet - interceptIfNoCredits shows appropriate modal per user type
		if (interceptIfNoCredits({ kind: 'chatEdit', designSystemId: dsChat.designSystemId })) {
			return;
		}

		const updateRequest: api.UpdateDsChatRequest = {
			update: {
				id: dsChat.id,
				metadata: {
					status: ChatStatus.PROCESSING,
					statusMessage: '',
				},
			},
		};

		await forge.updateDsChat(updateRequest);

		const entry: api.AddUserEntryDsChatRequest['entry'] = {
			kind: 'user',
			message: prompt,
			attachments: pendingDsChatInput?.attachments || [],
			...(selectedDsPicks.length > 0 && { selectedDsPicks: [...selectedDsPicks] }),
			...(activeTab && { viewingDsPick: dsTabToPick(activeTab) }),
		};

		const req: api.AddUserEntryDsChatRequest = {
			dsChatId: dsChat.id,
			entry,
			chatMode: pendingDsChatInput?.chatMode || ChatMode.DESIGN_AND_CHAT,
		};
		await forge.dsChatAddUserEntry.withToast()(req);

		setPrompt('');
		dispatch(clearDsPicks());

		dispatch(
			setPendingDsChatInput({
				dsChatId: dsChat.id,
				pendingDsChatInput: {
					...pendingDsChatInput,
					message: '',
					attachments: [],
				},
			})
		);
	};

	const onStop = async () => {
		if (!activeDesignSystem || !dsChat) return;

		await forge.updateDsChat({
			update: {
				id: dsChat.id,
				metadata: {
					status: ChatStatus.PROCESSING,
					statusMessage: 'Stopping...',
				},
			},
		});
		const req: api.StopDsChatGenerationRequest = {
			dsChatId: dsChat.id,
		};
		await forge.stopDsChatGeneration.withToast()(req);
	};

	// Callback for updating prompt - handles both local state and Redux
	const onUpdatePrompt = useCallback(
		(prompt: string) => {
			setPrompt(prompt);
			dispatch(
				setPendingDsChatInput({
					dsChatId: dsChat.id,
					pendingDsChatInput: {
						...pendingDsChatInput,
						message: prompt,
					},
				})
			);
		},
		[setPrompt, dispatch, dsChat.id, pendingDsChatInput]
	);

	// Shared input handlers
	const { onKeyDown, onContextMenu } = useChatInputHandlers({
		prompt,
		inputRef,
		canSubmit,
		onSubmit,
		onUpdatePrompt,
	});

	const onRemoveAttachment = (storagePath: string) => {
		dispatch(
			setPendingDsChatInput({
				dsChatId: dsChat.id,
				pendingDsChatInput: {
					...pendingDsChatInput,
					attachments: pendingDsChatInput.attachments?.filter((a) => a.storagePath !== storagePath),
				},
			})
		);
	};

	// File upload callbacks using Redux dispatch
	const fileCallbacks = useFileUploadCallbacks({
		type: 'ds',
		chatId: dsChat.id,
	});

	const onPickClick = (pick: DsPick, name: string) => {
		const target = dsPickToNavTarget(pick);
		if (!target || !activeDesignSystem) return;

		const tabId = target.contentPath ? `${target.section}-${target.contentPath}` : `section-${target.section}`;
		const existingTab = dsTabs.find((t) => t.id === tabId);
		if (existingTab) {
			dispatch(setActiveDsTab(tabId));
		} else {
			dispatch(
				addDsTab({
					id: tabId,
					section: target.section,
					contentPath: target.contentPath,
					label: name,
					showBackButton: !!target.contentPath,
				})
			);
		}

		const url = routes.designSystem(activeDesignSystem.id, activeDesignSystem.title);
		const params = new URLSearchParams({ section: target.section });
		if (target.contentPath) params.set('contentPath', target.contentPath);
		navigate(`${url}?${params.toString()}`);
	};

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------
	const renderDsPicks = () => {
		if (resolvedDsPicks.length === 0) return null;
		return (
			<Base.DsPicks>
				{resolvedDsPicks.map(({ pick, name }) => {
					const key =
						pick.kind === 'token'
							? `token-${pick.cssVariable}`
							: pick.kind === 'recipe'
							? `recipe-${pick.className}`
							: pick.kind === 'component'
							? `comp-${pick.tagName}`
							: pick.kind === 'guideline'
							? `guideline-${pick.guidelinePath}`
							: pick.kind === 'gallery'
							? `gallery-${pick.galleryPath}`
							: pick.kind === 'section'
							? `section-${pick.section}`
							: `asset-${pick.assetPath}`;
					return (
						<Pill
							key={key}
							label={name}
							tooltip="Click to open in design system"
							onClick={(e) => onPickClick(pick, name)}
							onRemove={() => dispatch(removeDsPick(pick))}
						/>
					);
				})}
				{resolvedDsPicks.length > 3 && (
					<Base.ClearAllButton onClick={() => dispatch(clearDsPicks())}>
						Clear all
					</Base.ClearAllButton>
				)}
			</Base.DsPicks>
		);
	};

	const renderAttachments = () => {
		const allAttachments = [
			...(pendingDsChatInput?.attachments || []),
			...(pendingDsChatInput?.loadingAttachments || []).map((attachment) => ({
				...attachment,
				storagePath: `loading-${attachment.uniqueId}`, // Use uniqueId for loading attachments
			})),
		];

		if (!allAttachments.length) return null;
		return (
			<Base.Attachments
				onWheel={(e: React.WheelEvent) => {
					e.stopPropagation();
					e.currentTarget.scrollLeft += e.deltaX;
				}}
			>
				{allAttachments.map((attachment) => {
					const loadingAttachment = pendingDsChatInput?.loadingAttachments?.find(
						(a) => a.uniqueId === attachment.storagePath.replace('loading-', '')
					);
					const isLoading = !!loadingAttachment;
					return (
						<ChatAttachmentTile
							key={attachment.storagePath}
							attachment={attachment}
							onDelete={isLoading ? undefined : () => onRemoveAttachment(attachment.storagePath)}
							state={isLoading ? ChatAttachmentTileState.LOADING : ChatAttachmentTileState.REGULAR}
							previewUrl={loadingAttachment?.previewUrl}
						/>
					);
				})}
			</Base.Attachments>
		);
	};

	const renderAttachmentButton = () => {
		return (
			<ActionButton
				icon={IconType.ADD}
				label={undefined}
				tooltip="Upload attachment"
				onClick={() => {
					onUploadFileDialog(fileCallbacks, true);
				}}
			/>
		);
	};

	const renderChatModeToggle = () => {
		const isChatOnly = pendingDsChatInput?.chatMode === ChatMode.CHAT_ONLY;
		return (
			<ActionButton
				state={isChatOnly ? ActionButtonState.SELECTED : ActionButtonState.REGULAR}
				icon={isChatOnly ? IconType.CHAT : IconType.STYLE}
				tooltip={isChatOnly ? 'Turn off Chat-only Mode' : 'Turn on Chat-only Mode'}
				colors={{
					selected: theme.green[200],
					hover: isChatOnly ? theme.green[400] : theme.gray[200],
					active: 'transparent',
					text: isChatOnly ? theme.green[800] : theme.gray[1000],
				}}
				onClick={() => {
					const nextChatMode =
						pendingDsChatInput?.chatMode === ChatMode.DESIGN_AND_CHAT
							? ChatMode.CHAT_ONLY
							: ChatMode.DESIGN_AND_CHAT;
					dispatch(
						setPendingDsChatInput({
							dsChatId: dsChat.id,
							pendingDsChatInput: {
								...pendingDsChatInput,
								chatMode: nextChatMode,
							},
						})
					);

					if (isMobile) {
						showToast(
							nextChatMode === ChatMode.CHAT_ONLY ? 'Chat-only mode' : 'Chat + design mode'
						);
					}
				}}
			/>
		);
	};

	const renderActions = () => {
		return (
			<Base.Actions isMobile={isMobile}>
				{!isMobile && renderAttachmentButton()}
				{renderChatModeToggle()}
				<Base.Spacer />
				{showSpinner ? (
					<Base.LoadingWrapper isMobile={isMobile}>
						<Loader width={isMobile ? 32 : 22} height={isMobile ? 32 : 22} />
					</Base.LoadingWrapper>
				) : (
					<Base.SubmitButtonWrapper isMobile={isMobile}>
						<SubmitButton
							state={
								stoppable || canSubmit ? SubmitButtonState.REGULAR : SubmitButtonState.DISABLED
							}
							colors={
								pendingDsChatInput?.chatMode === ChatMode.CHAT_ONLY
									? {
											disabled: theme.green[200],
											hover: theme.green[600],
											active: theme.green[800],
									  }
									: undefined
							}
							type={stoppable ? IconType.STOP : IconType.LINE_ARROW_UP}
							tooltip={stoppable ? 'Stop' : 'Submit'}
							tooltipPlacement="bottom-start"
							onClick={() => (stoppable ? onStop() : onSubmit())}
						/>
					</Base.SubmitButtonWrapper>
				)}
			</Base.Actions>
		);
	};

	return (
		<>
			{isMobile && toastMessage && (
				<Base.MobileToast isVisible={isToastVisible} role="status" aria-live="polite">
					{toastMessage}
				</Base.MobileToast>
			)}
			<Base.Base isMobile={isMobile}>
				<BreakageNotification dsChat={dsChat} />
				<Base.Prompt isFocused={isFocused}>
					{renderDsPicks()}
					{renderAttachments()}
					<Base.MultilineInputWrapper isMobile={isMobile} onPaste={onFilePasted(fileCallbacks)}>
						<MultilineInput
							data-ds-chat-input="true"
							ref={inputRef}
							value={prompt}
							onChange={(e) => onUpdatePrompt(e.currentTarget.value)}
							onKeyDown={onKeyDown}
							placeholder={'Chat about your design system'}
							minLines={isMobile ? 1 : 4}
							renderBorder={false}
							variant={isMobile ? 'large' : 'medium'}
							onFocusChange={(isFocused) => {
								setIsFocused(isFocused);
							}}
							onContextMenu={onContextMenu}
						/>
					</Base.MultilineInputWrapper>
					{renderActions()}
				</Base.Prompt>
			</Base.Base>
		</>
	);
};

export default DsChatInput;
